import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom helper icon
const helperIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" width="24" height="24">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

// User location icon
const userIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#10B981" width="24" height="24">
      <circle cx="12" cy="12" r="8" fill="#10B981" stroke="#fff" stroke-width="2"/>
      <circle cx="12" cy="12" r="3" fill="#fff"/>
    </svg>
  `),
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
});

// Component to handle map events
const MapEventHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng);
    },
  });
  return null;
};

// Component to update map center
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
};

const MapView = ({ 
  helpers = [], 
  onHelperSelect, 
  onLocationSelect,
  searchLocation,
  userLocation,
  height = '400px'
}) => {
  const [mapCenter, setMapCenter] = useState([28.6139, 77.2090]); // Default to Delhi
  const [mapZoom, setMapZoom] = useState(10);
  const [selectedHelper, setSelectedHelper] = useState(null);
  const [showUserLocation, setShowUserLocation] = useState(false);
  const mapRef = useRef();

  // Update map center when search location changes
  useEffect(() => {
    if (searchLocation) {
      geocodeLocation(searchLocation);
    }
  }, [searchLocation]);

  // Update map to show user location
  useEffect(() => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
      setMapZoom(13);
      setShowUserLocation(true);
    }
  }, [userLocation]);

  // Simple geocoding using a free service (in production, you'd use a proper geocoding service)
  const geocodeLocation = async (location) => {
    try {
      // Using Nominatim (OpenStreetMap's geocoding service) - free but rate limited
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location + ', India')}&limit=1`
      );
      const data = await response.json();
      
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setMapCenter([lat, lon]);
        setMapZoom(12);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter([latitude, longitude]);
          setMapZoom(13);
          setShowUserLocation(true);
          if (onLocationSelect) {
            onLocationSelect({ lat: latitude, lng: longitude });
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  // Generate helper locations - use real coordinates if available, otherwise generate random ones
  const generateHelperLocations = (helpers) => {
    return helpers.map(helper => {
      // Use real coordinates if available
      if (helper.coordinates && helper.coordinates.coordinates && helper.coordinates.coordinates.length === 2) {
        return {
          ...helper,
          coordinates: { 
            lat: helper.coordinates.coordinates[1], // MongoDB stores [lng, lat]
            lng: helper.coordinates.coordinates[0] 
          }
        };
      }
      
      // Fallback: Generate random coordinates within a reasonable radius of the map center
      const lat = mapCenter[0] + (Math.random() - 0.5) * 0.1;
      const lng = mapCenter[1] + (Math.random() - 0.5) * 0.1;
      return {
        ...helper,
        coordinates: { lat, lng }
      };
    });
  };

  const helpersWithLocations = generateHelperLocations(helpers);

  const handleHelperClick = (helper) => {
    setSelectedHelper(helper);
    if (onHelperSelect) {
      onHelperSelect(helper);
    }
  };

  const handleLocationClick = (latlng) => {
    if (onLocationSelect) {
      onLocationSelect(latlng);
    }
  };

  return (
    <div className="map-view">
      <div className="map-controls">
        <button 
          className="location-btn"
          onClick={getCurrentLocation}
          title="Get my location"
        >
          📍 My Location
        </button>
        <div className="map-legend">
          <div className="legend-item">
            <span className="legend-icon helper">📍</span>
            <span>Helpers</span>
          </div>
          {showUserLocation && (
            <div className="legend-item">
              <span className="legend-icon user">🎯</span>
              <span>You</span>
            </div>
          )}
        </div>
      </div>

      <div className="map-container" style={{ height }}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <MapUpdater center={mapCenter} zoom={mapZoom} />
          <MapEventHandler onLocationSelect={handleLocationClick} />
          
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Helper markers */}
          {helpersWithLocations.map(helper => (
            <Marker
              key={helper._id}
              position={[helper.coordinates.lat, helper.coordinates.lng]}
              icon={helperIcon}
              eventHandlers={{
                click: () => handleHelperClick(helper),
              }}
            >
              <Popup>
                <div className="helper-popup">
                  <h4>{helper.name}</h4>
                  <p>📍 {helper.location}</p>
                  <p>💰 ₹{helper.hourlyRate}/hour</p>
                  <p>⭐ {helper.rating?.toFixed(1) || 'No rating'}</p>
                  <div className="popup-services">
                    {helper.services.slice(0, 2).map(service => (
                      <span key={service} className="service-tag-small">
                        {service}
                      </span>
                    ))}
                    {helper.services.length > 2 && (
                      <span className="service-tag-small">+{helper.services.length - 2} more</span>
                    )}
                  </div>
                  <button 
                    className="popup-book-btn"
                    onClick={() => handleHelperClick(helper)}
                  >
                    View Profile
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* User location marker */}
          {showUserLocation && userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={userIcon}
            >
              <Popup>
                <div className="user-popup">
                  <h4>📍 Your Location</h4>
                  <p>This is your current location</p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      <div className="map-info">
        <p>
          🗺️ Click on the map to search for helpers in that area • 
          📍 Click on markers to view helper details
        </p>
      </div>
    </div>
  );
};

export default MapView; 